import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AuthConfig,
  getEnumName,
  RunCode,
  RunCodes,
  ScheduleItem,
  service,
  useAuthConfig,
} from "@theprelude/core";
import { scheduleQueryOptions } from "@/queries/schedule";

export interface ScheduledRow {
  id: string;
  name: string;
  isScheduled: true;
  target: string[] | null;
  runCode: RunCode;
}

export interface UnscheduledRow {
  id: string;
  name: string;
  isScheduled: false;
}

export type Schedule = ScheduledRow | UnscheduledRow;

interface UpsertSchedules {
  rows: Schedule[];
  type: "threat" | "test";
  tags?: string[] | null;
  runCode?: RunCode | null;
}

const getRowRunCode = (row: Schedule, runCode?: RunCode | null) => {
  if (runCode) {
    return runCode;
  }

  if (row.isScheduled && row.runCode) {
    return row.runCode;
  }

  throw new Error("Run code is required for unscheduled rows");
};

const getRowTags = (row: Schedule, tags?: string[] | null) => {
  if (tags) {
    return tags.join(",");
  }

  if (tags === null) {
    return "";
  }

  if (row.isScheduled && row.target !== undefined) {
    return row.target?.join(",") ?? "";
  }

  throw new Error("Target is required for unscheduled rows");
};

async function unscheduleRows(
  authConfig: AuthConfig,
  rows: ScheduledRow[],
  type: "threat" | "test",
) {
  const items = rows.map((row) => {
    return {
      type,
      id: row.id,
      tags: row.target?.join(",") ?? "",
    };
  });

  await service(authConfig).detect.unschedule(items);
}

async function scheduleRows(authConfig: AuthConfig, items: ScheduleItem[]) {
  await service(authConfig).detect.schedule(items);
}

export const useUpsertSchedules = () => {
  const authConfig = useAuthConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ rows, type, tags, runCode }: UpsertSchedules) => {
      const unschedules = rows.filter(
        (row): row is ScheduledRow => row.isScheduled,
      );

      const items = rows.map((row) => {
        return {
          type,
          id: row.id,
          runCode: getEnumName(RunCodes, getRowRunCode(row, runCode)),
          tags: getRowTags(row, tags),
        };
      });

      if (unschedules.length > 0) {
        await unscheduleRows(authConfig, unschedules, type);
      }

      await scheduleRows(authConfig, items);
    },
    onSettled: () => {
      void scheduleQueryOptions(authConfig).invalidate(queryClient);
    },
  });
};

interface DeleteSchedules {
  rows: ScheduledRow[];
  type: "threat" | "test";
}

export const useDeleteSchedules = () => {
  const queryClient = useQueryClient();
  const authConfig = useAuthConfig();
  return useMutation({
    mutationFn: async ({ rows, type }: DeleteSchedules) => {
      return unscheduleRows(authConfig, rows, type);
    },
    onSettled: () => {
      void scheduleQueryOptions(authConfig).invalidate(queryClient);
    },
  });
};
